<template>
	<div
		class="rich-markdown-editor"
		:class="{ 'is-disabled': disabled, 'is-fill': fill }"
		@touchstart.capture="trackTouchResizeStart">
		<div class="editor-toolbar">
			<v-btn
				icon="mdi-format-bold"
				title="Bold"
				density="compact"
				variant="text"
				:color="isActive('bold') ? 'primary' : undefined"
				:disabled="disabled"
				@click="toggleBold"
			/>
			<v-btn
				icon="mdi-format-italic"
				title="Italic"
				density="compact"
				variant="text"
				:color="isActive('italic') ? 'primary' : undefined"
				:disabled="disabled"
				@click="toggleItalic"
			/>
			<v-btn
				icon="mdi-format-underline"
				title="Underline"
				density="compact"
				variant="text"
				:color="isActive('underline') ? 'primary' : undefined"
				:disabled="disabled"
				@click="toggleUnderline"
			/>
			<v-btn
				icon="mdi-format-list-bulleted"
				title="Bulleted List"
				density="compact"
				variant="text"
				:color="isActive('bulletList') ? 'primary' : undefined"
				:disabled="disabled"
				@click="toggleBulletList"
			/>
			<v-btn
				icon="mdi-format-list-numbered"
				title="Numbered List"
				density="compact"
				variant="text"
				:color="isActive('orderedList') ? 'primary' : undefined"
				:disabled="disabled"
				@click="toggleOrderedList"
			/>
			<v-btn
				icon="mdi-format-list-checks"
				title="Task List"
				density="compact"
				variant="text"
				:color="isActive('taskList') ? 'primary' : undefined"
				:disabled="disabled"
				@click="toggleTaskList"
			/>
			<v-btn
				icon="mdi-format-quote-close"
				title="Block Quote"
				density="compact"
				variant="text"
				:color="isActive('blockquote') ? 'primary' : undefined"
				:disabled="disabled"
				@click="toggleBlockquote"
			/>
			<v-btn
				icon="mdi-code-tags"
				title="Code Block"
				density="compact"
				variant="text"
				:color="isActive('codeBlock') ? 'primary' : undefined"
				:disabled="disabled"
				@click="toggleCodeBlock"
			/>
			<v-divider vertical class="mx-1" />
			<v-btn
				icon="mdi-table-plus"
				title="Insert Table"
				density="compact"
				variant="text"
				:disabled="disabled || isInTable"
				@click="insertTable"
			/>
			<v-btn
				icon="mdi-table-row-plus-after"
				title="Add Row"
				density="compact"
				variant="text"
				:disabled="disabled || !isInTable"
				@click="addRowAfter"
			/>
			<v-btn
				icon="mdi-table-column-plus-after"
				title="Add Column"
				density="compact"
				variant="text"
				:disabled="disabled || !isInTable"
				@click="addColumnAfter"
			/>
			<v-btn
				icon="mdi-table-row-remove"
				title="Delete Row"
				density="compact"
				variant="text"
				:disabled="disabled || !isInTable"
				@click="deleteRow"
			/>
			<v-btn
				icon="mdi-table-column-remove"
				title="Delete Column"
				density="compact"
				variant="text"
				:disabled="disabled || !isInTable"
				@click="deleteColumn"
			/>
			<v-divider vertical class="mx-1" />
			<v-menu v-model="linkPopoverOpen" :close-on-content-click="false" location="bottom" @update:model-value="prepareLinkPopover">
				<template #activator="{ props: activatorProps }">
					<v-btn
						v-bind="activatorProps"
						icon="mdi-link-variant"
						title="Set Link"
						density="compact"
						variant="text"
						:color="isActive('link') ? 'primary' : undefined"
						:disabled="disabled"
					/>
				</template>
				<v-card data-test-id="rich-editor-link-popover" class="editor-input-popover" rounded="lg" @click.stop>
					<v-card-text class="pb-0">
						<v-text-field
							v-model="linkUrl"
							data-test-id="rich-editor-link-url"
							label="Link URL"
							variant="outlined"
							density="compact"
							autofocus
							@keydown.enter.prevent="applyLink"
							@keydown.esc.prevent="closeLinkPopover"
						/>
					</v-card-text>
					<v-card-actions class="justify-end pt-0">
						<v-btn data-test-id="rich-editor-link-cancel" variant="text" @click="closeLinkPopover">Cancel</v-btn>
						<v-btn data-test-id="rich-editor-link-apply" color="primary" variant="flat" @click="applyLink">Apply</v-btn>
					</v-card-actions>
				</v-card>
			</v-menu>
			<v-menu v-model="imagePopoverOpen" :close-on-content-click="false" location="bottom" @update:model-value="prepareImagePopover">
				<template #activator="{ props: activatorProps }">
					<v-btn
						v-bind="activatorProps"
						icon="mdi-image-outline"
						title="Insert or Replace Image"
						density="compact"
						variant="text"
						:disabled="disabled"
					/>
				</template>
				<v-card data-test-id="rich-editor-image-popover" class="editor-input-popover" rounded="lg" @click.stop>
					<v-card-text class="pb-0">
						<v-text-field
							v-model="imageUrl"
							data-test-id="rich-editor-image-url"
							label="Image URL"
							variant="outlined"
							density="compact"
							autofocus
							@keydown.enter.prevent="applyImage"
							@keydown.esc.prevent="closeImagePopover"
						/>
					</v-card-text>
					<v-card-actions class="justify-end pt-0">
						<v-btn data-test-id="rich-editor-image-cancel" variant="text" @click="closeImagePopover">Cancel</v-btn>
						<v-btn data-test-id="rich-editor-image-apply" color="primary" variant="flat" @click="applyImage">Apply</v-btn>
					</v-card-actions>
				</v-card>
			</v-menu>
			<v-menu
				v-model="imageWidthPopoverOpen"
				:close-on-content-click="false"
				location="bottom"
				@update:model-value="prepareImageWidthPopover"
			>
				<template #activator="{ props: activatorProps }">
					<v-btn
						v-bind="activatorProps"
						icon="mdi-arrow-expand-horizontal"
						title="Resize Selected Image"
						density="compact"
						variant="text"
						:disabled="disabled || !isImageSelected"
					/>
				</template>
				<v-card data-test-id="rich-editor-image-width-popover" class="editor-input-popover" rounded="lg" @click.stop>
					<v-card-text class="pb-0">
						<v-text-field
							v-model="imageWidth"
							data-test-id="rich-editor-image-width"
							label="Image width in pixels"
							type="text"
							inputmode="numeric"
							variant="outlined"
							density="compact"
							autofocus
							:error-messages="imageWidthError"
							hint="Leave blank to reset the image size."
							persistent-hint
							@update:model-value="imageWidthError = ''"
							@keydown.enter.prevent="applyImageWidth"
							@keydown.esc.prevent="closeImageWidthPopover"
						/>
					</v-card-text>
					<v-card-actions class="justify-end pt-0">
						<v-btn data-test-id="rich-editor-image-width-cancel" variant="text" @click="closeImageWidthPopover">Cancel</v-btn>
						<v-btn data-test-id="rich-editor-image-width-apply" color="primary" variant="flat" @click="applyImageWidth">Apply</v-btn>
					</v-card-actions>
				</v-card>
			</v-menu>
			<v-btn
				icon="mdi-image-size-select-actual"
				title="Reset Image Size"
				data-test-id="rich-editor-image-reset-size"
				density="compact"
				variant="text"
				:disabled="disabled || !isImageSelected"
				@click="resetImageSize"
			/>
			<v-btn
				icon="mdi-delete-outline"
				title="Delete Selected Image"
				density="compact"
				variant="text"
				:disabled="disabled || !isImageSelected"
				@click="removeImage"
			/>
			<v-divider vertical class="mx-1" />
			<v-btn
				icon="mdi-undo"
				title="Undo"
				density="compact"
				variant="text"
				:disabled="disabled || !canUndo"
				@click="undo"
			/>
			<v-btn
				icon="mdi-redo"
				title="Redo"
				density="compact"
				variant="text"
				:disabled="disabled || !canRedo"
				@click="redo"
			/>
		</div>

		<EditorContent :editor="editor" class="editor-content scribe-analysis-viewer" />
	</div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { EditorContent, getHTMLFromFragment, useEditor } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import { Table, TableCell, TableHeader, TableRow, renderTableToMarkdown } from '@tiptap/extension-table'
import TaskList from '@tiptap/extension-task-list'
import TaskItem from '@tiptap/extension-task-item'
import { Markdown } from '@tiptap/markdown'
import { deleteTableColumn, deleteTableRow } from '@/components/scribe/utils/tableCommands'

const props = withDefaults(defineProps<{
	modelValue: string
	disabled?: boolean
	/**
	 * LSP-20051: let the editor grow to fill a definite-height flex parent (the report dialog)
	 * instead of capping its content area at 60vh, which left half the dialog empty.
	 */
	fill?: boolean
}>(), {
	disabled: false,
	fill: false,
})

const emit = defineEmits<{
	'update:modelValue': [value: string]
}>()

type TouchResizeGesture = {
	identifier: number
	direction: 'bottom-left' | 'bottom-right'
	container: HTMLElement
	image: HTMLImageElement
	nodePosition: number
	startX: number
	startWidth: number
	aspectRatio: number
}

type TextSelectionSnapshot = {
	from: number
	to: number
}

type ImageSelectionSnapshot = {
	position: number
	currentWidth: number
	currentHeight: number | null
}

const linkPopoverOpen = ref(false)
const linkUrl = ref('')
let linkSelection: TextSelectionSnapshot | null = null

const imagePopoverOpen = ref(false)
const imageUrl = ref('')
let imageSelection: (TextSelectionSnapshot & { imagePosition: number | null }) | null = null

const imageWidthPopoverOpen = ref(false)
const imageWidth = ref('')
const imageWidthError = ref('')
let imageWidthSelection: ImageSelectionSnapshot | null = null

let touchResizeGesture: TouchResizeGesture | null = null

function removeTouchResizeListeners() {
	document.removeEventListener('touchmove', resizeTouchMove)
	document.removeEventListener('touchend', finishTouchResize)
	document.removeEventListener('touchcancel', finishTouchResize)
}

function clearTouchResize() {
	removeTouchResizeListeners()
	if (touchResizeGesture) touchResizeGesture.container.dataset.resizeState = 'false'
	touchResizeGesture = null
}

function trackTouchResizeStart(event: TouchEvent) {
	if (!(event.target instanceof Element) || touchResizeGesture) return
	const handle = event.target.closest<HTMLElement>('[data-resize-handle]')
	const direction = handle?.dataset.resizeHandle
	if (!handle || (direction !== 'bottom-left' && direction !== 'bottom-right')) return
	const container = handle.closest<HTMLElement>('[data-resize-container]')
	const image = container?.querySelector<HTMLImageElement>('[data-resize-wrapper] img')
	const initiatingTouch = event.changedTouches[0] ?? event.touches[0]
	if (!container || !image || !initiatingTouch || !editor.value) return
	const nodePosition = editor.value.view.posAtDOM(container, 0)
	if (editor.value.state.doc.nodeAt(nodePosition)?.type.name !== 'image') return

	event.preventDefault()
	event.stopPropagation()
	const startWidth = image.offsetWidth
	const startHeight = image.offsetHeight
	touchResizeGesture = {
		identifier: initiatingTouch.identifier,
		direction,
		container,
		image,
		nodePosition,
		startX: initiatingTouch.clientX,
		startWidth,
		aspectRatio: startWidth > 0 && startHeight > 0 ? startWidth / startHeight : 1,
	}
	container.dataset.resizeState = 'true'
	document.addEventListener('touchmove', resizeTouchMove, { passive: false })
	document.addEventListener('touchend', finishTouchResize)
	document.addEventListener('touchcancel', finishTouchResize)
}

function resizeTouchMove(event: TouchEvent) {
	if (!touchResizeGesture) return
	const touch = Array.from(event.touches).find(candidate => candidate.identifier === touchResizeGesture?.identifier)
	if (!touch) return
	event.preventDefault()

	const deltaX = touch.clientX - touchResizeGesture.startX
	let width = touchResizeGesture.startWidth + (touchResizeGesture.direction === 'bottom-right' ? deltaX : -deltaX)
	let height = width / touchResizeGesture.aspectRatio
	if (width < 80) {
		width = 80
		height = width / touchResizeGesture.aspectRatio
	}
	if (height < 80) {
		height = 80
		width = height * touchResizeGesture.aspectRatio
	}
	touchResizeGesture.image.style.width = `${width}px`
	touchResizeGesture.image.style.height = `${height}px`
}

function finishTouchResize(event: TouchEvent) {
	if (!touchResizeGesture
		|| !Array.from(event.changedTouches).some(touch => touch.identifier === touchResizeGesture?.identifier)) return
	const { image, nodePosition } = touchResizeGesture
	const width = image.offsetWidth
	const height = image.offsetHeight
	clearTouchResize()
	editor.value
		?.chain()
		.setNodeSelection(nodePosition)
		.updateAttributes('image', { width, height })
		.run()
}

function isImageNodeSelection(selection: unknown): selection is {
	from: number
	node: { type: { name: string }; attrs: Record<string, unknown> }
} {
	if (!selection || typeof selection !== 'object') return false
	const maybeSelection = selection as {
		node?: { type?: { name?: string }; attrs?: Record<string, unknown> }
		from?: number
	}
	return (
		typeof maybeSelection.from === 'number'
		&& !!maybeSelection.node
		&& maybeSelection.node.type?.name === 'image'
		&& !!maybeSelection.node.attrs
	)
}

function escapeHtmlAttribute(value: string): string {
	return value
		.replaceAll('&', '&amp;')
		.replaceAll('"', '&quot;')
		.replaceAll('<', '&lt;')
		.replaceAll('>', '&gt;')
}

function isPlainTextKeyEvent(event: KeyboardEvent): boolean {
	return event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey
}

const RichImage = Image.extend({
	renderMarkdown: (node: any) => {
		const src = node.attrs?.src ?? ''
		const alt = node.attrs?.alt ?? ''
		const title = node.attrs?.title ?? ''
		const width = node.attrs?.width
		const height = node.attrs?.height

		if (width || height) {
			const attributes = [
				`src="${escapeHtmlAttribute(String(src))}"`,
				`alt="${escapeHtmlAttribute(String(alt))}"`,
			]

			if (title) {
				attributes.push(`title="${escapeHtmlAttribute(String(title))}"`)
			}
			if (width) {
				attributes.push(`width="${Number(width)}"`)
			}
			if (height) {
				attributes.push(`height="${Number(height)}"`)
			}

			return `<img ${attributes.join(' ')} />`
		}

		return title ? `![${alt}](${src} "${title}")` : `![${alt}](${src})`
	},
})

interface MarkdownTableJson {
	type: string
	content?: Array<{ content?: Array<{ attrs?: { colwidth?: unknown } }> }>
}

function tableHasColumnWidths(node: MarkdownTableJson): boolean {
	return (node.content ?? []).some((row) =>
		(row.content ?? []).some((cell) => {
			const colwidth = cell.attrs?.colwidth
			return Array.isArray(colwidth) && colwidth.some((width) => typeof width === 'number' && width > 0)
		}),
	)
}

// LSP-20060: a GFM pipe table has no syntax for column widths, so every drag of a column
// boundary used to be thrown away by getMarkdown() on save. Once the user has resized a
// column, serialize the table as HTML instead (the same fallback RichImage already uses for
// `<img width>`): @tiptap/markdown re-parses HTML blocks through the table extensions, so the
// `colwidth` attributes survive a reopen, and the `<colgroup>` keeps the widths in the
// read-only viewer and the PDF. A table that was never resized still serializes as markdown.
// Mirrors scribe-admin's RichMarkdownEditor (the two editors share this markdown contract).
const RichTable = Table.extend({
	renderMarkdown(node, helpers) {
		const schema = editor.value?.schema
		if (!schema || !tableHasColumnWidths(node as MarkdownTableJson)) {
			return renderTableToMarkdown(node, helpers)
		}
		const doc = schema.nodeFromJSON({ type: 'doc', content: [node] })
		// Table.renderHTML also writes the summed widths onto the <table> as an inline `width` or,
		// while some columns are still unsized, `min-width`. Persisting that inline minimum would
		// re-introduce the off-screen overflow LSP-20058 fixes (inline min-width beats the viewer's
		// max-width cap), so keep only the <colgroup> hints and let the layout scale them.
		return getHTMLFromFragment(doc.content, schema).replace(/^<table([^>]*?)\sstyle="[^"]*"/, '<table$1')
	},
})

const isImageSelected = ref(false)

const editor = useEditor({
	extensions: [
		StarterKit.configure({ link: false }),
		Link.configure({
			openOnClick: false,
			autolink: true,
			linkOnPaste: true,
			defaultProtocol: 'https',
		}),
		RichImage.configure({
			inline: true,
			resize: {
				enabled: true,
				directions: ['bottom-left', 'bottom-right'],
				minWidth: 80,
				minHeight: 80,
				alwaysPreserveAspectRatio: true,
			},
		}),
		RichTable.configure({
			resizable: true,
		}),
		TableRow,
		TableHeader,
		TableCell,
		TaskList,
		TaskItem.configure({
			nested: true,
		}),
		Markdown,
	],
	content: props.modelValue || '',
	contentType: 'markdown',
	editable: !props.disabled,
	editorProps: {
		handleKeyDown: (view, event) => {
			const selection = view.state.selection
			if (!isImageNodeSelection(selection)) return false
			if (!isPlainTextKeyEvent(event)) return false

			event.preventDefault()
			const tr = view.state.tr.insertText(event.key, selection.to)
			view.dispatch(tr)
			return true
		},
	},
	onUpdate: ({ editor: tiptapEditor }) => {
		emit('update:modelValue', tiptapEditor.getMarkdown())
	},
	onSelectionUpdate: ({ editor: tiptapEditor }) => {
		isImageSelected.value = isImageNodeSelection(tiptapEditor.state.selection)
	},
})

const canUndo = computed(() => editor.value?.can().chain().focus().undo().run() ?? false)
const canRedo = computed(() => editor.value?.can().chain().focus().redo().run() ?? false)
const isInTable = computed(() => editor.value?.isActive('table') ?? false)

watch(
	() => props.modelValue,
	(value) => {
		if (!editor.value) return
		const nextValue = value || ''
		if (editor.value.getMarkdown() === nextValue) return
		closeLinkPopover()
		closeImagePopover()
		closeImageWidthPopover()
		editor.value.commands.setContent(nextValue, { contentType: 'markdown' })
	}
)

watch(
	() => props.disabled,
	(disabled) => {
		if (disabled) {
			closeLinkPopover()
			closeImagePopover()
			closeImageWidthPopover()
		}
		editor.value?.setEditable(!disabled)
	}
)

function isActive(name: string): boolean {
	return editor.value?.isActive(name) ?? false
}

function toggleBold() {
	editor.value?.chain().focus().toggleBold().run()
}

function toggleItalic() {
	editor.value?.chain().focus().toggleItalic().run()
}

function toggleUnderline() {
	editor.value?.chain().focus().toggleUnderline().run()
}

function toggleBulletList() {
	editor.value?.chain().focus().toggleBulletList().run()
}

function toggleOrderedList() {
	editor.value?.chain().focus().toggleOrderedList().run()
}

function toggleTaskList() {
	editor.value?.chain().focus().toggleTaskList().run()
}

function toggleBlockquote() {
	editor.value?.chain().focus().toggleBlockquote().run()
}

function toggleCodeBlock() {
	editor.value?.chain().focus().toggleCodeBlock().run()
}

function insertTable() {
	editor.value?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
}

function addRowAfter() {
	editor.value?.chain().focus().addRowAfter().run()
}

function addColumnAfter() {
	editor.value?.chain().focus().addColumnAfter().run()
}

function deleteRow() {
	deleteTableRow(editor.value)
}

function deleteColumn() {
	deleteTableColumn(editor.value)
}

function getSelectedImageInfo():
	| {
			pos: number
			node: { attrs: Record<string, unknown> }
		}
	| null {
	if (!editor.value) return null
	const selection = editor.value.state.selection
	if (!isImageNodeSelection(selection)) {
		return null
	}

	return {
		pos: selection.from,
		node: selection.node,
	}
}

function getSelectedImageDom(pos: number): HTMLImageElement | null {
	if (!editor.value) return null
	const nodeDom = editor.value.view.nodeDOM(pos)
	if (!nodeDom || !(nodeDom instanceof HTMLElement)) return null

	if (nodeDom.tagName.toLowerCase() === 'img') {
		return nodeDom as HTMLImageElement
	}

	return nodeDom.querySelector('img')
}

function prepareLinkPopover(open: boolean) {
	if (!open || props.disabled || !editor.value) return
	const selection = editor.value.state.selection
	linkSelection = { from: selection.from, to: selection.to }
	linkUrl.value = (editor.value.getAttributes('link').href as string | undefined) || 'https://'
}

function closeLinkPopover() {
	linkPopoverOpen.value = false
	linkSelection = null
}

function applyLink() {
	if (props.disabled || !editor.value || !linkSelection) return
	const chain = editor.value.chain().focus().setTextSelection(linkSelection).extendMarkRange('link')
	if (linkUrl.value === '') {
		chain.unsetLink().run()
	} else {
		chain.setLink({ href: linkUrl.value }).run()
	}
	closeLinkPopover()
}

function prepareImagePopover(open: boolean) {
	if (!open || props.disabled || !editor.value) return
	const selection = editor.value.state.selection
	const selectedImage = getSelectedImageInfo()
	imageSelection = {
		from: selection.from,
		to: selection.to,
		imagePosition: selectedImage?.pos ?? null,
	}
	imageUrl.value = (selectedImage?.node.attrs?.src as string | undefined) || ''
}

function closeImagePopover() {
	imagePopoverOpen.value = false
	imageSelection = null
}

function applyImage() {
	if (props.disabled || !editor.value || !imageSelection) return
	const url = imageUrl.value.trim()
	if (!url) {
		closeImagePopover()
		return
	}

	if (imageSelection.imagePosition !== null) {
		editor.value
			.chain()
			.focus()
			.setNodeSelection(imageSelection.imagePosition)
			.updateAttributes('image', { src: url })
			.run()
	} else {
		editor.value.chain().focus().setTextSelection(imageSelection).setImage({ src: url }).run()
	}
	closeImagePopover()
}

function prepareImageWidthPopover(open: boolean) {
	if (!open || props.disabled || !editor.value) return
	const selectedImage = getSelectedImageInfo()
	if (!selectedImage) {
		imageWidthPopoverOpen.value = false
		return
	}

	const attrs = selectedImage.node.attrs as {
		width?: number | null
		height?: number | null
	}
	const domImage = getSelectedImageDom(selectedImage.pos)
	const firstPositiveDimension = (...dimensions: Array<number | undefined>) =>
		dimensions.find((dimension) => Number.isFinite(dimension) && Number(dimension) > 0) ?? null
	const currentWidth = firstPositiveDimension(Number(attrs.width), domImage?.clientWidth, domImage?.naturalWidth) ?? 150
	const currentHeight = firstPositiveDimension(Number(attrs.height), domImage?.clientHeight, domImage?.naturalHeight)
	imageWidthSelection = {
		position: selectedImage.pos,
		currentWidth,
		currentHeight,
	}
	imageWidth.value = String(Math.round(currentWidth))
	imageWidthError.value = ''
}

function closeImageWidthPopover() {
	imageWidthPopoverOpen.value = false
	imageWidthError.value = ''
	imageWidthSelection = null
}

// LSP-20069: clear the persisted width/height so the image renders at its natural size and
// aspect ratio again. The resizable node view keeps the inline px styles it applied during the
// drag even after the attributes are cleared (its `update` hook only re-checks the node type),
// so clear those styles too or the reset would not be visible until the editor re-mounts.
function clearImageSize(position: number) {
	if (!editor.value) return
	editor.value
		.chain()
		.focus()
		.setNodeSelection(position)
		.updateAttributes('image', { width: null, height: null })
		.run()
	const domImage = getSelectedImageDom(position)
	if (domImage) {
		domImage.style.width = ''
		domImage.style.height = ''
	}
}

function resetImageSize() {
	if (props.disabled || !editor.value) return
	const selectedImage = getSelectedImageInfo()
	if (!selectedImage) return
	clearImageSize(selectedImage.pos)
}

function applyImageWidth() {
	if (props.disabled || !editor.value || !imageWidthSelection) return
	const trimmed = imageWidth.value.trim()
	if (!trimmed) {
		clearImageSize(imageWidthSelection.position)
		closeImageWidthPopover()
		return
	}

	const nextWidth = Math.round(Number(trimmed))
	if (!Number.isFinite(nextWidth) || nextWidth <= 0) {
		imageWidthError.value = 'Enter a positive number.'
		return
	}

	const ratio = imageWidthSelection.currentHeight && imageWidthSelection.currentWidth
		? imageWidthSelection.currentHeight / imageWidthSelection.currentWidth
		: null
	const nextHeight = ratio ? Math.round(nextWidth * ratio) : null
	editor.value
		.chain()
		.focus()
		.setNodeSelection(imageWidthSelection.position)
		.updateAttributes('image', { width: nextWidth, height: nextHeight })
		.run()
	closeImageWidthPopover()
}

function removeImage() {
	editor.value?.chain().focus().deleteSelection().run()
}

function undo() {
	editor.value?.chain().focus().undo().run()
}

function redo() {
	editor.value?.chain().focus().redo().run()
}

defineExpose({ editor })

onBeforeUnmount(() => {
	clearTouchResize()
	editor.value?.destroy()
})
</script>

<style scoped>
.rich-markdown-editor {
	border: 1px solid rgba(var(--v-theme-on-surface), 0.2);
	border-radius: 8px;
	background-color: rgb(var(--v-theme-surface));
}

.is-disabled {
	opacity: 0.8;
}

.editor-toolbar {
	display: flex;
	align-items: center;
	gap: 2px;
	padding: 6px;
	border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.12);
	flex-wrap: wrap;
}

.editor-input-popover {
	width: 20rem;
	max-width: calc(100vw - 2rem);
}

.editor-content {
	min-height: 240px;
	max-height: 60vh;
	overflow-y: auto;
	padding: 12px;
}

/* LSP-20051: inside the report dialog the editor is a flex child of a definite-height card, so
   let it take that height and scroll internally instead of stopping at 60vh. */
.is-fill {
	display: flex;
	flex: 1 1 auto;
	flex-direction: column;
	min-height: 0;
}

.is-fill .editor-content {
	flex: 1 1 auto;
	max-height: none;
	min-height: 0;
}

:deep(.ProseMirror) {
	outline: none;
	min-height: 214px;
	white-space: pre-wrap;
	font-family: inherit;
	font-size: inherit;
}

:deep(.ProseMirror .tableWrapper) {
	margin: 0.5rem 0;
	max-width: 100%;
	overflow-x: auto;
}

/* LSP-20058: prosemirror-tables writes the summed column widths onto the table as an inline
   `width` (or `min-width` while some columns are still unsized), so dragging one boundary far
   enough pushed the whole table past the editor. Chromium ignores `max-width` on a fixed-layout
   table but honours it under the default auto layout, scaling the columns to fit — so leave the
   layout auto, cap the table at the editor width and neutralise the inline `min-width` floor.
   The wrapping rules on the cells let the auto layout shrink a column below an unbroken word. */
:deep(.ProseMirror table) {
	max-width: 100%;
	min-width: 0 !important;
}

:deep(.ProseMirror td),
:deep(.ProseMirror th) {
	position: relative;
	min-width: 0;
	overflow-wrap: anywhere;
}

/* LSP-20054: prosemirror-tables toggles `.resize-cursor` on the editor root and inserts a
   `.column-resize-handle` into the hovered cell, but ships no CSS for either — without these
   rules the boundary is draggable yet gives no cursor or handle feedback. */
:deep(.ProseMirror.resize-cursor) {
	cursor: col-resize;
}

:deep(.ProseMirror .column-resize-handle) {
	position: absolute;
	top: 0;
	right: -2px;
	bottom: -2px;
	width: 4px;
	pointer-events: none;
	background-color: rgb(var(--v-theme-primary));
}

/* LSP-20069: the resizable node view writes both `width` and `height` in px onto the <img>.
   Once the width is capped by the editor edge the fixed height stretched the image, and the
   distortion persisted into the read-only viewer through the saved `height` attribute. Let the
   height follow the rendered width. */
:deep(.ProseMirror [data-resize-wrapper] img),
:deep(.ProseMirror img[width]),
:deep(.ProseMirror img[height]) {
	max-width: 100%;
	height: auto !important;
}

:deep(.ProseMirror ul[data-type='taskList']) {
	list-style: none;
	padding-left: 0;
}

:deep(.ProseMirror ul[data-type='taskList'] li) {
	display: flex;
	align-items: flex-start;
	gap: 8px;
}

:deep(.ProseMirror [data-resize-container]) {
	display: inline-flex;
	max-width: 100%;
}

:deep(.ProseMirror [data-resize-wrapper]) {
	max-width: 100%;
}

:deep(.ProseMirror [data-resize-handle]) {
	width: 10px;
	height: 10px;
	border-radius: 999px;
	border: 1px solid rgb(var(--v-theme-surface));
	background-color: rgb(var(--v-theme-primary));
	z-index: 2;
}

:deep(.ProseMirror [data-resize-handle='bottom-left']) {
	transform: translate(-50%, 50%);
	cursor: nesw-resize;
}

:deep(.ProseMirror [data-resize-handle='bottom-right']) {
	transform: translate(50%, 50%);
	cursor: nwse-resize;
}
</style>
